import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { createIsomorphicFn } from "@tanstack/react-start";
import { useRouter } from "@tanstack/react-router";
import {
  DEFAULT_COUNTRY,
  DEFAULT_LANG,
  htmlLang,
  isLang,
  isSellCountry,
  readCookie,
  writeCookie,
  LANG_COOKIE,
  COUNTRY_COOKIE,
  type Lang,
  type SellCountry,
} from "./config";
import { siblingLangPath } from "./slug-map";

type LocaleValue = {
  lang: Lang;
  country: SellCountry;
  setLanguage: (lang: Lang) => void;
  setCountry: (c: SellCountry) => void;
};

const LocaleContext = createContext<LocaleValue>({
  lang: DEFAULT_LANG,
  country: DEFAULT_COUNTRY,
  setLanguage: () => {},
  setCountry: () => {},
});

export function LocaleProvider({
  lang,
  country,
  children,
}: {
  lang: Lang;
  country: SellCountry;
  children: ReactNode;
}) {
  const router = useRouter();
  const [activeCountry, setActiveCountry] = useState(country);

  useEffect(() => {
    document.documentElement.lang = htmlLang(lang);
  }, [lang]);

  useEffect(() => {
    setActiveCountry(country);
  }, [country]);

  const value = useMemo<LocaleValue>(
    () => ({
      lang,
      country: activeCountry,
      setLanguage: (next) => {
        writeCookie(LANG_COOKIE, next);
        const destination = siblingLangPath(window.location.pathname, next);
        void router.navigate({
          href: `${destination}${window.location.search}${window.location.hash}`,
          resetScroll: false,
        });
      },
      setCountry: (c) => {
        setActiveCountry(c);
        writeCookie(COUNTRY_COOKIE, c);
        // Läs om landberoende siddata utan en full omladdning. AuthProvider ligger
        // kvar monterad, så en pågående inloggning påverkas inte av marknadsvalet.
        void router.invalidate();
      },
    }),
    [lang, activeCountry, router],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleValue {
  return useContext(LocaleContext);
}

/** Läser kakor ur förfrågan (server) respektive document.cookie (webbläsare). */
const readLocaleCookies = createIsomorphicFn()
  .server(async (): Promise<{ lang: string | null; country: string | null }> => {
    try {
      const { getRequest } = await import("@tanstack/react-start/server");
      const req = getRequest();
      const cookieHeader = req.headers.get("cookie") ?? "";
      const parse = (name: string) =>
        cookieHeader.match(new RegExp(`(?:^|; )${name}=([^;]*)`))?.[1] ?? null;
      return { lang: parse(LANG_COOKIE), country: parse(COUNTRY_COOKIE) };
    } catch {
      return { lang: null, country: null };
    }
  })
  .client(async (): Promise<{ lang: string | null; country: string | null }> => ({
    lang: readCookie(LANG_COOKIE),
    country: readCookie(COUNTRY_COOKIE),
  }));

/**
 * Läser aktuellt språk/land vid uppstart. Rootens beforeLoad anropar den.
 * Språk följer sidans prefix (/en, /es) först, sedan kakan – roten är svensk.
 */
export async function resolveInitialLocale(pathname: string): Promise<{
  lang: Lang;
  country: SellCountry;
}> {
  const { lang: langCookie, country: countryCookie } = await readLocaleCookies();

  // Språk ur URL-prefixet vinner (de tre språken har egna adresser).
  const pathLang = pathname.startsWith("/en/")
    ? "en"
    : pathname.startsWith("/es/")
      ? "es"
      : pathname === "/en" || pathname === "/es"
        ? (pathname.slice(1) as Lang)
        : null;
  // Roten är alltid svenska – cookie gäller bara om besökaren själv valt
  // språk (kakan skrivs när man byter språk).
  const lang: Lang = isLang(pathLang)
    ? pathLang
    : isLang(langCookie)
      ? langCookie
      : DEFAULT_LANG;

  const country: SellCountry = isSellCountry(countryCookie) ? countryCookie : DEFAULT_COUNTRY;
  return { lang, country };
}
